import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type Token, type ChainflipToken } from '@/shared/assets/tokens';
import useBoost from '@/shared/hooks/useBoost';
import useDebouncedEffect from '@/shared/hooks/useDebouncedEffect';
import { integrationManager, type RouteResponse } from '@/shared/integrations';
import type TokenAmount from '@/shared/utils/TokenAmount';

export const useSwapRoutes = ({
  showRouteList,
  srcToken,
  destToken,
  srcAmount,
  isOnChain,
}: {
  showRouteList: boolean;
  srcToken: ChainflipToken | Token;
  destToken: ChainflipToken | Token;
  srcAmount?: TokenAmount;
  isOnChain: boolean;
}) => {
  const { boostActive } = useBoost();
  const queryClient = useQueryClient();

  const queryKey = [
    'route-list',
    srcToken?.chain.id,
    destToken?.chain.id,
    srcToken?.address,
    destToken?.address,
    srcAmount?.toString(),
    isOnChain,
    boostActive,
  ];

  const queryFn = async () => {
    if (srcToken && destToken && srcAmount) {
      const routes = await integrationManager.getRoutes({
        srcChainId: srcToken.chain.id,
        destChainId: destToken.chain.id,
        srcTokenAddress: srcToken.address,
        destTokenAddress: destToken.address,
        amount: BigInt(srcAmount.toString()),
        isOnChain,
      });

      return routes
        .filter((route) => {
          if (route.integration !== 'chainflip') return true;
          if (!boostActive) return !route.integrationData.isBoosted;
          return route.integrationData.isBoosted || !route.integrationData.hasBoostQuote;
        })
        .sort((a, b) => b.destAmount.cmp(a.destAmount));
    }
    return [];
  };

  const query = useQuery<RouteResponse[]>({
    queryKey,
    queryFn,
    enabled: false,
    refetchInterval: 30000,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  useDebouncedEffect(
    ({ signal }) => {
      if (!showRouteList || signal.aborted) return;

      queryClient.cancelQueries({ queryKey });
      query.refetch();
    },
    [showRouteList, srcToken, destToken, srcAmount, isOnChain, boostActive],
    700,
  );

  return query;
};
